import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Drawer, Icon, Spinner } from "@aigntx/ui";
import { endpoints } from "./api";
import { useTenant } from "./tenant";
import { useAuth } from "./auth";
import { useNotify } from "./notify";
import { ViewAsPill } from "./ViewAsPill";
import { ThemeControls } from "./ThemeControls";
import { BrandMark } from "./BrandMark";
import { useInstallPrompt } from "./pwa";
import type { AppNotification } from "./types";

const linkCls = ({ isActive }: { isActive: boolean }) =>
  isActive ? "topnav__link is-active" : "topnav__link";

export function Layout() {
  const tenant = useTenant();
  const { user, logout, can } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { available: canInstall, install } = useInstallPrompt();
  const { notify } = useNotify();

  useEffect(() => {
    if (!notify) {
      setUnread(0);
      return;
    }
    endpoints
      .unreadCount()
      .then((r) => setUnread(r.count))
      .catch(() => {});
  }, [notify]);

  const links = [
    { to: "/", end: true, label: t("app.start"), show: true },
    { to: "/dashboard", label: t("app.dashboard"), show: true },
    { to: "/projects", label: t("app.projects"), show: tenant.modules.includes("eng.projects") },
    { to: "/records", label: t("app.records"), show: tenant.modules.includes("crm") },
    { to: "/data", label: t("app.data"), show: true },
    { to: "/guide", label: t("app.guide"), show: true },
    { to: "/company", label: t("app.company"), show: can("users.read") || can("tenant.manage") },
    { to: "/settings", label: t("app.settings"), show: true },
    { to: "/admin", label: t("app.admin"), show: Boolean(user?.isPlatformAdmin) },
    { to: "/canvas", label: t("app.canvas"), show: Boolean(user?.isPlatformAdmin) },
  ].filter((l) => l.show);

  async function signOut() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="shell">
      <ViewAsPill />
      <header className="topbar">
        <button className="topbar__burger" onClick={() => setNavOpen(true)} aria-label={t("app.menu")}>
          <Icon name="menu" size={18} />
        </button>
        <div className="brand">
          {tenant.theme.logo ? (
            <img src={tenant.theme.logo} alt="" className="brand__logo" />
          ) : (
            <BrandMark icon={tenant.theme.icon} />
          )}
          <span className="brand__name">{tenant.name}</span>
        </div>

        <nav className="topnav topnav--desktop">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkCls}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="topbar__right">
          <ThemeControls />
          <button
            className={notify ? "iconbtn" : "iconbtn is-muted"}
            onClick={() => setNotifOpen(true)}
            aria-label={t("notif.title")}
            title={notify ? t("notif.title") : t("notif.mutedHint")}
          >
            <Icon name="bell" size={17} />
            {notify && unread > 0 && (
              <span className="iconbtn__badge">{unread > 9 ? "9+" : unread}</span>
            )}
            {!notify && <span className="iconbtn__mute" />}
          </button>
          {user && (
            <button className="userbtn" onClick={() => setMenuOpen(true)} aria-label={t("app.account")}>
              <Avatar src={user.avatarUrl || undefined} name={user.name} size={30} />
            </button>
          )}
        </div>
      </header>

      <main className="content">
        <Outlet />
      </main>

      <Drawer open={navOpen} onClose={() => setNavOpen(false)} side="start" title={tenant.name}>
        <nav className="drawer-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className="drawer-nav__link"
              onClick={() => setNavOpen(false)}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </Drawer>

      <Drawer open={notifOpen} onClose={() => setNotifOpen(false)} side="end" title={t("notif.title")}>
        <NotificationsPanel onUnreadChange={setUnread} onClose={() => setNotifOpen(false)} />
      </Drawer>

      <Drawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        side="end"
        title={t("app.account")}
        width="min(320px, 92vw)"
      >
        {user && (
          <div className="usermenu">
            <div className="usermenu__id">
              <Avatar src={user.avatarUrl || undefined} name={user.name} size={46} />
              <div>
                <div className="usermenu__name">{user.name}</div>
                <div className="usermenu__mail mono">{user.email}</div>
              </div>
            </div>
            <button
              className="usermenu__item"
              onClick={() => {
                setMenuOpen(false);
                navigate("/settings");
              }}
            >
              {t("app.settings")}
            </button>
            {(can("users.read") || can("tenant.manage")) && (
              <button
                className="usermenu__item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/company");
                }}
              >
                {t("app.company")}
              </button>
            )}
            {canInstall && (
              <button
                className="usermenu__item"
                onClick={() => {
                  setMenuOpen(false);
                  void install();
                }}
              >
                {t("app.install")}
              </button>
            )}
            <button className="usermenu__item usermenu__item--danger" onClick={() => void signOut()}>
              {t("app.signOut")}
            </button>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function NotificationsPanel({
  onUnreadChange,
  onClose,
}: {
  onUnreadChange: (n: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify, setNotify } = useNotify();
  const [items, setItems] = useState<AppNotification[] | null>(null);

  useEffect(() => {
    endpoints
      .listNotifications()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  async function readAll() {
    await endpoints.markAllNotificationsRead();
    setItems((it) => (it ? it.map((n) => ({ ...n, read: true })) : it));
    onUnreadChange(0);
  }

  async function open(n: AppNotification, link: string) {
    if (!n.read) {
      const r = await endpoints.markNotificationRead(n.id);
      setItems((it) => (it ? it.map((x) => (x.id === n.id ? { ...x, read: true } : x)) : it));
      onUnreadChange(r.count);
    }
    // Being told a form needs you is only half of it — take them to it.
    if (link) {
      onClose();
      navigate(link);
    }
  }

  const muteRow = (
    <div className="notif-mute">
      <span>{notify ? t("notif.onLabel") : t("notif.offLabel")}</span>
      <Button size="sm" variant="outline" onClick={() => setNotify(!notify)}>
        {notify ? t("notif.turnOff") : t("notif.turnOn")}
      </Button>
    </div>
  );

  if (!items)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );
  if (items.length === 0)
    return (
      <div className="notif-list">
        {muteRow}
        <p className="muted">{t("notif.empty")}</p>
      </div>
    );

  return (
    <div className="notif-list">
      {muteRow}
      <div className="notif-list__top">
        <Button size="sm" variant="ghost" onClick={() => void readAll()}>
          {t("notif.readAll")}
        </Button>
      </div>
      {items.map((n) => {
        // A notification with a `kind` is rendered here, in the reader's own
        // language. Ones stored as prose keep their stored words — an older row
        // written in English stays readable rather than becoming a blank.
        const title = n.kind ? t(`notif.${n.kind}.title`, n.data) : n.title;
        const msg = n.kind ? t(`notif.${n.kind}.body`, n.data) : n.body;
        const link =
          n.data?.projectId && n.data?.formId
            ? `/projects/${n.data.projectId}/forms/${n.data.formId}`
            : "";
        return (
          <button
            key={n.id}
            className={n.read ? "notif" : "notif is-unread"}
            onClick={() => void open(n, link)}
          >
            <span className={`notif__dot notif__dot--${n.tone}`} />
            <span className="notif__body">
              <span className="notif__title">{title}</span>
              <span className="notif__msg">{msg}</span>
              <span className="notif__time mono">{new Date(n.createdAt).toLocaleString()}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
